import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Maximize2 } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   MÓDULOS DEL SHOWCASE
   Para cada módulo: badge (etiqueta corta), titulo (breve), desc (1-2
   líneas) e img. Poné tus capturas en  frontend/public/showcase/  con
   el nombre que figura en `img` (ej: public/showcase/dashboard.png).
   Podés agregar, quitar o reordenar libremente.
──────────────────────────────────────────────────────────────────── */
const MODULOS = [
  {
    id: 'dashboard',
    badge: 'Dashboard',
    titulo: 'Panel de Control',
    desc: 'KPIs en vivo: facturación mensual, causas activas y tasa de cobranza. Producción por abogado y estado de causas de un vistazo.',
    img: '/showcase/dashboard.png',
  },
  {
    id: 'expedientes',
    badge: 'Expedientes',
    titulo: 'Vista 360° de cada causa',
    desc: 'Datos del caso, movimientos procesales, honorarios y bóveda de evidencias — todo en un solo panel.',
    img: '/showcase/expedientes.png',
  },
  {
    id: 'agenda',
    badge: 'Agenda',
    titulo: 'Calendario con Google Sync',
    desc: 'Audiencias y vencimientos en un calendario propio, sincronizado con Google Calendar en dos clics.',
    img: '/showcase/agenda.png',
  },
  {
    id: 'facturacion',
    badge: 'Facturación',
    titulo: 'Honorarios y cuotas',
    desc: 'Honorarios con plan de cuotas, registro de pagos, comprobantes en PDF y estado de cobro por cliente.',
    img: '/showcase/facturacion.png',
  },
  {
    id: 'asistente',
    badge: 'Asistente IA',
    titulo: 'Redactá escritos con IA',
    desc: 'Analizá expedientes, corregí y redactá demandas y escritos con IA especializada en derecho argentino, conectada al contexto de tu causa.',
    img: '/showcase/asistente.png',
  },
  {
    id: 'sincronizacion',
    badge: 'Sincronización Judicial',
    titulo: 'Portales judiciales conectados',
    desc: 'Traé los movimientos de tus expedientes automáticamente desde los portales del Poder Judicial.',
    img: '/showcase/sincronizacion.png',
  },
];

/* Marco de "ventana" del navegador, reutilizado en tarjeta y lightbox */
function Ventana({ titulo, children, className = '' }) {
  return (
    <div className={`bg-[#0c0c0c] rounded-xl border border-[rgba(201,164,76,0.18)] overflow-hidden ${className}`}>
      <div className="bg-[#1a1a1a] px-3 py-1.5 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-red-500/60" />
        <span className="w-2 h-2 rounded-full bg-yellow-500/60" />
        <span className="w-2 h-2 rounded-full bg-green-500/60" />
        <span className="text-[#B8B8B8]/50 text-[9px] ml-2 truncate">LexOrbit — {titulo}</span>
      </div>
      {children}
    </div>
  );
}

/* Imagen con fallback mientras no exista la captura */
function Captura({ src, alt }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className="w-full aspect-[16/10] flex flex-col items-center justify-center gap-2 bg-[#0e0e0e] text-[#B8B8B8]/40 text-xs">
        <Maximize2 className="w-6 h-6 opacity-40" />
        Captura próximamente
      </div>
    );
  }
  // aspect reservado (igual que el placeholder): evita layout-shift al cargar la
  // imagen lazy, que corría el scroll de los anchors (#planes) hacia arriba.
  return <img src={src} alt={alt} onError={() => setError(true)} loading="lazy" className="w-full aspect-[16/10] object-cover block" />;
}

export default function ShowcaseSection({ onTrialClick }) {
  const [activo, setActivo] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="demo" className="relative py-20 lg:py-28 bg-[#080808]" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Encabezado */}
        <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center mb-16">
          <span className="text-[#C9A44C] text-[11px] uppercase tracking-[0.2em] block mb-3">El sistema</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-[#F8F6F0] tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Un sistema real para la gestión diaria de <span className="text-[#C9A44C] italic">tu estudio</span>
          </h2>
          <p className="text-[#B8B8B8] text-sm max-w-xl mx-auto leading-relaxed">
            Recorré los módulos: a medida que bajás, cada pantalla se va revelando. Tocá un título para verlo en grande.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[0.85fr_1.35fr] gap-8 lg:gap-14">
          {/* ── Columna de títulos (sticky en desktop) ── */}
          <div className="hidden lg:block">
            <div className="sticky top-28 space-y-1.5">
              {MODULOS.map((m, i) => {
                const on = activo === i;
                return (
                  <button key={m.id} onClick={() => setLightbox(m)}
                    className={`w-full text-left rounded-xl px-4 py-3.5 border transition-all duration-300 ${
                      on
                        ? 'border-[rgba(201,164,76,0.5)] bg-[#C9A44C]/[0.06] shadow-[0_0_25px_rgba(201,164,76,0.07)]'
                        : 'border-transparent hover:border-[rgba(201,164,76,0.18)] hover:bg-white/[0.02]'
                    }`}
                    data-testid={`showcase-title-${i}`}>
                    <span className={`text-[9px] uppercase tracking-[0.18em] font-semibold block mb-1 transition-colors ${on ? 'text-[#C9A44C]' : 'text-[#B8B8B8]/40'}`}>
                      {String(i + 1).padStart(2, '0')} · {m.badge}
                    </span>
                    <h3 className={`text-[15px] font-semibold transition-colors ${on ? 'text-[#F8F6F0]' : 'text-[#B8B8B8]/70'}`}
                      style={{ fontFamily: 'Playfair Display, serif' }}>
                      {m.titulo}
                    </h3>
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }} className="overflow-hidden">
                          <p className="text-[#B8B8B8] text-[11.5px] leading-relaxed mt-1.5">{m.desc}</p>
                          <span className="inline-flex items-center gap-1 text-[#C9A44C] text-[10px] font-medium mt-2">
                            <Maximize2 className="w-3 h-3" /> Ver en grande
                          </span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Columna de imágenes (scroll escalonado) ── */}
          <div className="space-y-14 lg:space-y-28">
            {MODULOS.map((m, i) => (
              // Revelado: se anima una sola vez al entrar y queda visible (once).
              <motion.div key={m.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                className={`lg:max-w-[94%] ${i % 2 ? 'lg:ml-auto' : 'lg:mr-auto'}`}>
                {/* Sentinel separado: sincroniza el título activo con el scroll (sin once). */}
                <motion.div
                  onViewportEnter={() => setActivo(i)}
                  viewport={{ margin: '-45% 0px -45% 0px' }}
                  className="group cursor-pointer"
                  onClick={() => setLightbox(m)}
                  data-testid={`showcase-image-${i}`}>
                  {/* Título visible solo en mobile (en desktop está en la columna sticky) */}
                  <div className="lg:hidden mb-3">
                    <span className="text-[#C9A44C] text-[9px] uppercase tracking-[0.18em] font-semibold block mb-0.5">{String(i + 1).padStart(2, '0')} · {m.badge}</span>
                    <h3 className="text-[#F8F6F0] text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{m.titulo}</h3>
                    <p className="text-[#B8B8B8] text-[11.5px] leading-relaxed mt-1">{m.desc}</p>
                  </div>
                  <Ventana titulo={m.titulo}
                    className="transition-all duration-500 group-hover:border-[rgba(201,164,76,0.45)] group-hover:shadow-[0_0_35px_rgba(201,164,76,0.1)]">
                    <div className="overflow-hidden">
                      <div className="transition-transform duration-500 group-hover:scale-[1.02]">
                        <Captura src={m.img} alt={m.titulo} />
                      </div>
                    </div>
                  </Ventana>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
          className="text-center mt-20">
          <p className="text-[#B8B8B8] text-sm mb-5">Probá el sistema completo desde tu propia organización</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={onTrialClick}
              className="group text-[#050505] px-7 py-3.5 rounded text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(201,164,76,0.35)]"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}
              data-testid="showcase-trial-btn">
              Probar gratis 5 días <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <a href="#planes" className="border border-[rgba(201,164,76,0.3)] text-[#F8F6F0] px-7 py-3.5 rounded text-sm font-medium hover:border-[#C9A44C]/60 hover:bg-white/[0.03] transition-all duration-300 text-center">Ver planes</a>
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
            onClick={() => setLightbox(null)} data-testid="showcase-lightbox">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-5xl w-full" onClick={e => e.stopPropagation()}>
              <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-[#B8B8B8] hover:text-[#F8F6F0] transition-colors" data-testid="close-showcase-lightbox">
                <X className="w-6 h-6" />
              </button>
              <Ventana titulo={lightbox.titulo} className="border-[rgba(201,164,76,0.25)]">
                <Captura src={lightbox.img} alt={lightbox.titulo} />
              </Ventana>
              <div className="mt-4 text-center">
                <span className="text-[#C9A44C] text-[10px] uppercase tracking-[0.15em] font-semibold">{lightbox.badge}</span>
                <p className="text-[#F8F6F0] text-lg font-semibold mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>{lightbox.titulo}</p>
                <p className="text-[#B8B8B8] text-sm mt-1 max-w-2xl mx-auto">{lightbox.desc}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
